// Import CSS
import './AssessmentFormScreen.scss'

// React and Third Party Libraries
import { useState, useCallback, useMemo } from 'react'
import cn from 'classnames'
import AssessmentFormCheckGroup from '../AssessmentFormCheckGroup'
import AssessmentFormSwitch from '../AssessmentFormSwitch'
import AssessmentFormOrderGroup from '../AssessmentFormOrderGroup'

/**
 * Assessment Form Screen component: used to render an assessment form screen (a screen is a collection of questions)
 * @param {Object} initProps - initial props
 * @param {Object} initProps.screenData - screen data to be used to build the questions. Must follow the assessment schema
 * Properties included in the screenData object:
 * - title: title of the screen
 * - description: description of the screen
 * - field-groups: array of questions
 * - css-class: css class to be applied to the screen
 * @param {Number} initProps.screenIndex - index of the screen
 * @param {Number} initProps.isVisible - boolean to indicate if the screen is visible
 * @param {Function} initProps.setScreenIsValid - function to set the screen as valid
 * @param {Function} initProps.handleAnswerChange - function to handle the change of an answer
 * @returns {JSX.Element} - Assessment Form component
 */
export default function AssessmentFormScreen({
  screenData,
  screenIndex,
  setScreenIsValid,
  handleAnswerChange,
  isVisible
}) {
  // State to store the answer values of all questions in the screen
  const [answerValues, setAnswerValues] = useState({})

  const fieldGroups = useMemo(
    () => screenData['field-groups'] || [],
    [screenData]
  )
  const cssClass = screenData['css-class'] || ''

  /**
   * Question factory: returns the JSX for a question, based on the question type
   * @param {Object} question Question data
   * @param {Number} questionIndex The index of the question
   */
  const questionFactory = (question, questionIndex) => {
    // If we don't have a question type, return null
    if (!question.type) return null

    const types = {
      'checkbox-group': AssessmentFormCheckGroup,
      'radio-group': AssessmentFormCheckGroup,
      'order-group': AssessmentFormOrderGroup,
      switch: AssessmentFormSwitch
    }

    const QuestionComponent = types[question.type]

    if (!QuestionComponent) return null

    return (
      <QuestionComponent
        questionData={question}
        questionIndex={questionIndex}
        handleInputChange={handleInputChange}
        key={questionIndex}
        isVisible={isVisible}
      />
    )
  }

  /**
   * Handle complete check: checks if all questions are answered and sets the screen as valid
   * @param {Object} values - object containing the answer values of all questions
   * @returns
   */
  const handleCompleteCheck = useCallback(
    (values = null) => {
      // If we don't have values, return
      if (!values) return

      // If the current screen is not the active screen, return
      if (!isVisible) return

      // Set the screen as complete if all questions are answered and no values are empty
      const allAnswerValues = Object.values(values)
      const allQuestionsAnswered =
        allAnswerValues.every(value => value) &&
        allAnswerValues.length === screenData['field-groups'].length

      setScreenIsValid(screenIndex, allQuestionsAnswered)
    },
    [screenIndex, isVisible, screenData, setScreenIsValid]
  )

  /**
   * Handle input change: updates the answer values and calls the handleCompleteCheck function
   * @param {Object} inputData - input data object
   * @param {String} inputData.name - name of the input
   * @param {String} inputData.value - value of the input
   * @param {Array} inputData.values - values of the input (for check groups)
   */
  const handleInputChange = ({ name = '', value = '', values = [] }) => {
    let updatedAnswerValues = {}
    let inputValue = value

    // If the question is a check group, we need to use the values array
    if (values.length) inputValue = values

    // If the value is empty, remove the question from the questionValues object
    if (!inputValue) {
      const { [name]: removedValue, ...rest } = answerValues
      updatedAnswerValues = rest
    } else {
      // Update the question values
      updatedAnswerValues = {
        ...answerValues,
        [name]: inputValue
      }
    }

    handleCompleteCheck(updatedAnswerValues)
    setAnswerValues(updatedAnswerValues)
    handleAnswerChange(name, inputValue)
  }

  // If we don't have screen data, return null
  if (!screenData) return null

  return (
    <div
      className={cn('assessment-form-screen', {
        'd-none': !isVisible,
        [cssClass]: cssClass
      })}
      key={screenIndex}
    >
      {screenData.title && (
        <h2 className='assessment-form-screen-title title'>
          {screenData.title}
        </h2>
      )}
      {screenData.description && (
        <p className='subtitle assessment-form-screen-description'>
          {screenData.description}
        </p>
      )}
      {fieldGroups.map((question, index) => questionFactory(question, index))}
    </div>
  )
}
